import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Picture from './Picture';
import { API_BASE_URL } from '../config.js';
import { getAdminBadge } from '../utils/adminBadge';
import { getFilterTranslations } from '../i18n/filters';
import LIMITS from '../access/exportLimits.json';

const sanitizeNamePart = (value) => value
  .toString()
  .toLowerCase()
  .replace(/[^a-zA-Z0-9]/g, '_')
  .replace(/\s+/g, '_');

const generateDefaultName = (filters, searchText) => {
  if (searchText) return sanitizeNamePart(searchText).replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 80);
  const filterParts = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'createdat' || key === 'favorite' || key === 'deleted' || key === 'source') return;

    if (Array.isArray(value)) {
      if (value.length > 0) {
        filterParts.push(value.map(v => sanitizeNamePart(v.label || v.value)).join('-'));
      }
    } else if (value?.label || value?.value) {
      filterParts.push(sanitizeNamePart(value.label || value.value));
    }
  });

  return filterParts.length > 0 ? filterParts.join('_') : 'photos_export';
};

/** The active filters as readable chips, shown in the panel and written in the « Recherche » sheet. */
const summarizeFilters = (filters, translations, t) => Object.entries(filters).flatMap(([key, value]) => {
  const name = translations[key] || key;
  if (key === 'createdat' && value?.startDate) return [`${name} : ${value.startDate} → ${value.endDate || '…'}`];
  if (key === 'latest_visit' && value === true) return [t('Latest visit per store')];
  if (key === 'favorite' && value === true) return [t('Favorites')];
  if (key === 'ai' && value && Object.keys(value).length) return [t('AI filters')];
  if (Array.isArray(value) && value.length) return [`${name} : ${value.map(v => v.label || v.value).join(', ')}`];
  if (value?.label || value?.value) return [`${name} : ${value.label || value.value}`];
  return [];
});

const ERROR_MESSAGES = {
  export_too_broad: 'This search is too broad to export: narrow it down.',
  latest_visit_too_broad: 'Latest visit is not available on such a broad search: narrow it down.',
  query_timeout: 'The search took too long: narrow it down and try again.',
  export_empty: 'No photo to export.'
};

const readError = async (response, t) => {
  const body = await response.json().catch(() => ({}));
  if (ERROR_MESSAGES[body.code]) return t(ERROR_MESSAGES[body.code]);
  if (response.status === 403) return t('You are not allowed to export these photos.');
  return body.error || t('Export failed, please try again.');
};

const download = (href, fileName) => {
  const link = document.createElement('a');
  link.href = href;
  if (fileName) link.download = fileName;
  else {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const FORMATS = [
  {
    id: 'xlsx',
    title: 'Data (Excel)',
    usage: 'Store listing, prices and promotions, compliance: one row per photo, ready to filter.',
    icon: 'M3 5h18v14H3V5zm0 4h18M9 5v14M15 5v14'
  },
  {
    id: 'zip',
    title: 'Photos (ZIP)',
    usage: 'The image files, named after their store and question.',
    icon: 'M4 7h16v12H4V7zm4 0V5h8v2m-6 5h4'
  },
  {
    id: 'pptx',
    title: 'Presentation (PowerPoint)',
    usage: 'Ready-made slides for a client meeting.',
    icon: 'M4 5h16v10H4V5zm8 10v4m-4 0h8'
  }
];

function ScopePicker({ scope, onChange, searchCount, selectionCount, searchExportable, disabled, t }) {
  const options = [
    { id: 'search', label: t('Whole search'), count: searchCount },
    { id: 'selection', label: t('My selection'), count: selectionCount }
  ];
  return (
    <div className="grid grid-cols-2 gap-1 rounded-2xl border border-gray-200 p-1 dark:border-gray-800">
      {options.map(option => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          disabled={disabled || (option.id === 'selection' && selectionCount === 0) || (option.id === 'search' && !searchExportable)}
          title={option.id === 'search' && !searchExportable ? t('Export available up to {{limit}} photos: narrow the search.', { limit: LIMITS.xlsx.toLocaleString() }) : undefined}
          aria-pressed={scope === option.id}
          className={[
            'rounded-xl px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40',
            scope === option.id
              ? 'bg-gray-900 text-white shadow-sm dark:bg-white dark:text-gray-950'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-white'
          ].join(' ')}
        >
          {option.label}
          <span className="ml-1.5 font-normal tabular-nums opacity-70">
            {option.count === null ? '…' : option.count.toLocaleString()}
          </span>
        </button>
      ))}
    </div>
  );
}

function FormatCards({ formats, format, onChange, availability, disabled, t }) {
  return (
    <div className="space-y-2">
      {formats.map(entry => {
        const reason = availability[entry.id];
        const selected = format === entry.id;
        return (
          <button
            key={entry.id}
            type="button"
            onClick={() => onChange(entry.id)}
            disabled={disabled || !!reason}
            aria-pressed={selected}
            className={[
              'flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition disabled:cursor-not-allowed',
              selected
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20 dark:border-blue-400 dark:bg-blue-950/40'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-900',
              reason ? 'opacity-60' : ''
            ].join(' ')}
          >
            <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300'}`}>
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d={entry.icon} />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{t(entry.title)}</span>
                <span className="shrink-0 text-xs text-gray-400">{t('up to {{count}}', { count: LIMITS[entry.id].toLocaleString() })}</span>
              </span>
              <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{t(entry.usage)}</span>
              {reason && <span className="mt-1 block text-xs font-medium text-amber-700 dark:text-amber-300">{reason}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const defaultColumnsOf = (sheets) => Object.fromEntries(sheets.map(sheet => [
  sheet.key, new Set(sheet.columns.filter(column => column.default).map(column => column.key))
]));

function ExcelOptions({ preview, loading, columns, onToggleColumn, onResetColumns, t }) {
  const [customizing, setCustomizing] = useState(false);
  if (loading) return <p className="text-sm text-gray-500 dark:text-gray-400">{t('Reading what the export will contain…')}</p>;
  if (!preview?.sheets?.length) return null;

  const picked = preview.sheets.filter(sheet => columns[sheet.key]?.size > 0);
  const hasAdminColumns = preview.sheets.some(sheet => sheet.columns.some(column => column.adminOnly));

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('Sheets')}</p>
      {picked.length === 0 ? (
        <p className="text-sm text-amber-700 dark:text-amber-300">{t('Pick at least one column.')}</p>
      ) : (
        <ul className="space-y-1">
          {picked.map(sheet => (
            <li key={sheet.key} className="rounded-lg bg-white px-2.5 py-1.5 text-sm dark:bg-gray-950">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center text-gray-800 dark:text-gray-100">{sheet.adminOnly && getAdminBadge()}{t(sheet.name)}</span>
                <span className="tabular-nums text-xs text-gray-500">{t('rows', { count: sheet.rows })}</span>
              </div>
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                {sheet.columns.filter(column => columns[sheet.key].has(column.key)).map(column => column.header).join(' · ')}
              </p>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() => setCustomizing(open => !open)}
        aria-expanded={customizing}
        className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
      >
        <svg className={`h-3 w-3 transition ${customizing ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {t('Customize columns')}
      </button>

      {customizing && (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-800 dark:bg-gray-950">
          {preview.sheets.map(sheet => (
            <fieldset key={sheet.key}>
              <legend className="mb-1 inline-flex items-center text-xs font-semibold text-gray-700 dark:text-gray-200">
                {sheet.adminOnly && getAdminBadge()}{t(sheet.name)}
              </legend>
              <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-2">
                {sheet.columns.map(column => (
                  <label key={column.key} className="flex min-w-0 items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={!!columns[sheet.key]?.has(column.key)}
                      onChange={() => onToggleColumn(sheet.key, column.key)}
                      className="h-3.5 w-3.5 rounded border-gray-300"
                    />
                    <span className="truncate">{column.header}</span>
                    {column.adminOnly && getAdminBadge()}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
          <div className="flex items-center justify-between border-t border-gray-100 pt-2 dark:border-gray-800">
            <button type="button" onClick={onResetColumns} className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-300">
              {t('Back to the default columns')}
            </button>
            <span className="text-xs text-gray-400">{t('Only columns holding at least one value are written.')}</span>
          </div>
          {hasAdminColumns && (
            <p className="inline-flex items-center text-xs text-gray-400">{getAdminBadge()}{t('Not in a client export: suffixed (admin) in the file.')}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Export drawer: what (the whole search or the selection), what for (Excel, ZIP, PowerPoint), then
 * the options of that format. Each format states its limit up front and says why it is unavailable,
 * instead of failing once clicked.
 */
function ExportPanel({
  isOpen,
  initialScope = 'search',
  initialFormat = null,
  searchText = null,
  filters,
  buildQuery,
  searchTotal,
  searchExportable = true,
  canExportPhotos = false,
  selectedPictures,
  canDelete,
  fetchWithAuth,
  onClose,
  onRemovePicture,
  onClearSelection,
  onDeleteSelected,
  onExported,
  t
}) {
  const translations = useMemo(() => getFilterTranslations(t), [t]);
  const [scope, setScope] = useState(initialScope);
  const [format, setFormat] = useState('xlsx');
  const [archiveName, setArchiveName] = useState('');
  const [pptxLayout, setPptxLayout] = useState(2);
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [columns, setColumns] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  const [showSelection, setShowSelection] = useState(false);

  const selectionIds = useMemo(() => selectedPictures.map(picture => picture.id), [selectedPictures]);
  const filtersSummary = useMemo(() => summarizeFilters(filters, translations, t), [filters, translations, t]);
  const defaultName = useMemo(() => generateDefaultName(filters, searchText), [filters, searchText]);

  useEffect(() => {
    if (!isOpen) return;
    const startingScope = (initialScope === 'selection' && selectedPictures.length > 0) || !searchExportable ? 'selection' : 'search';
    setScope(startingScope);
    setFormat(canExportPhotos ? initialFormat || (startingScope === 'selection' ? 'zip' : 'xlsx') : 'xlsx');
    setArchiveName(defaultName);
    setIncludeMetadata(true);
    setError('');
    setShowSelection(startingScope === 'selection');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialScope, initialFormat]);

  const scopeBody = useCallback(() => ({
    scope,
    query: buildQuery().toString(),
    ...(scope === 'selection' ? { pictureIds: selectionIds } : {})
  }), [scope, buildQuery, selectionIds]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (scope === 'selection' && selectionIds.length === 0) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setError('');
    fetchWithAuth(`${API_BASE_URL}/api/export/excel/preview`, { method: 'POST', body: JSON.stringify(scopeBody()) })
      .then(async response => {
        if (!response.ok) throw new Error(await readError(response, t));
        return response.json();
      })
      .then(data => {
        if (cancelled) return;
        setPreview(data);
        setColumns(defaultColumnsOf(data.sheets));
      })
      .catch(failure => { if (!cancelled) { setPreview(null); setError(failure.message); } })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, scope, scopeBody, selectionIds.length, fetchWithAuth, t]);

  if (!isOpen) return null;

  const count = scope === 'selection' ? selectionIds.length : (preview ? preview.total : null);
  const overExcel = scope === 'search' ? !!preview?.overLimit : count > LIMITS.xlsx;
  const searchIds = scope === 'search' ? preview?.ids : selectionIds;
  const tooMany = (limit) => t('{{count}} photos, limited to {{limit}}: narrow the search or select photos.', {
    count: overExcel ? `${LIMITS.xlsx.toLocaleString()}+` : (count ?? 0).toLocaleString(), limit
  });
  const availability = {
    xlsx: overExcel ? tooMany(LIMITS.xlsx.toLocaleString()) : null,
    zip: count !== null && (count > LIMITS.zip || (scope === 'search' && !previewLoading && preview && !searchIds)) ? tooMany(LIMITS.zip) : null,
    pptx: count !== null && count > LIMITS.pptx ? tooMany(LIMITS.pptx) : null
  };
  const noColumn = format === 'xlsx' && Object.values(columns).every(keys => keys.size === 0);
  const blocked = isExporting || previewLoading || !count || !!availability[format] || noColumn;
  const exportName = archiveName.trim() || defaultName;

  const toggleColumn = (sheetKey, columnKey) => setColumns(current => {
    const next = new Set(current[sheetKey] || []);
    if (next.has(columnKey)) next.delete(columnKey); else next.add(columnKey);
    return { ...current, [sheetKey]: next };
  });
  const pickedColumns = Object.fromEntries(Object.entries(columns).map(([sheetKey, keys]) => [sheetKey, [...keys]]));

  const runExport = async () => {
    setIsExporting(true);
    setError('');
    try {
      const response = format === 'xlsx'
        ? await fetchWithAuth(`${API_BASE_URL}/api/export/excel`, {
          method: 'POST',
          body: JSON.stringify({ ...scopeBody(), columns: pickedColumns, archiveName: exportName, searchText, filtersSummary })
        })
        : await fetchWithAuth(`${API_BASE_URL}/api/export-pictures`, {
          method: 'POST',
          body: JSON.stringify({
            pictureIds: searchIds, format, archiveName: exportName, pptxLayout,
            addMetadata: includeMetadata, includeMetadata
          })
        });
      if (!response.ok) throw new Error(await readError(response, t));

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.startsWith('image/')) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const filenameMatch = response.headers.get('content-disposition')?.match(/filename="(.+)"/);
        download(url, filenameMatch ? filenameMatch[1] : 'picture.jpg');
        window.URL.revokeObjectURL(url);
      } else {
        const { downloadUrl } = await response.json();
        download(`${window.location.origin}/downloads/${downloadUrl}`);
      }
      onExported?.({ format, scope, count });
    } catch (failure) {
      setError(failure.message || t('Export failed, please try again.'));
    } finally {
      setIsExporting(false);
    }
  };

  const actionLabel = {
    xlsx: t('Download Excel ({{count}} photos)', { count: (count ?? 0).toLocaleString() }),
    zip: t('Download ZIP ({{count}} photos)', { count: (count ?? 0).toLocaleString() }),
    pptx: t('Download PowerPoint ({{count}} photos)', { count: (count ?? 0).toLocaleString() })
  }[format];

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-950">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('Export')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
            title={t('Close')}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">{t('What?')}</h3>
            <ScopePicker
              scope={scope}
              onChange={(next) => { setScope(next); setShowSelection(next === 'selection'); }}
              searchCount={scope === 'search' && preview ? (preview.overLimit ? searchTotal : preview.total) : searchTotal}
              selectionCount={selectionIds.length}
              searchExportable={searchExportable}
              disabled={isExporting}
              t={t}
            />
            {scope === 'search' && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {searchText && (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950/60 dark:text-blue-200">✦ {searchText}</span>
                )}
                {filtersSummary.length === 0
                  ? <span className="text-xs text-gray-500 dark:text-gray-400">{t('No filter: every photo you can see.')}</span>
                  : filtersSummary.map(chip => (
                    <span key={chip} className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300">{chip}</span>
                  ))}
              </div>
            )}
            {scope === 'selection' && selectionIds.length === 0 && (
              <div className="mt-2 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                <p className="font-medium text-gray-700 dark:text-gray-200">{t('Your export cart is empty')}</p>
                <p>{t('Select photos from the gallery or from the large preview.')}</p>
                {!searchExportable && <p className="mt-1">{t('Export available up to {{limit}} photos: narrow the search.', { limit: LIMITS.xlsx.toLocaleString() })}</p>}
              </div>
            )}
            {scope === 'selection' && selectionIds.length > 0 && (
              <div className="mt-2">
                <button type="button" onClick={() => setShowSelection(open => !open)} className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-300">
                  {showSelection ? t('Hide selected photos') : t('Show selected photos')}
                </button>
                {showSelection && (
                  <div className="mt-2 max-h-64 space-y-2 overflow-y-auto">
                    {selectedPictures.map((picture) => (
                      <div key={picture.id} className="flex gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-2 dark:border-gray-800 dark:bg-gray-900/70">
                        <Picture pictureurl={picture.pictureurl} className="h-12 w-16 shrink-0 rounded-xl object-cover" lazy={true} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{picture.shopname || `#${picture.id}`}</p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{picture.translatedquestion || picture.question}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{picture.createdat ? dayjs(picture.createdat).format('L') : ''}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => onRemovePicture(picture.id)}
                          className="h-8 rounded-full p-2 text-gray-400 hover:bg-white hover:text-red-600 dark:hover:bg-gray-800"
                          title={t('Remove')}
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">{t('What for?')}</h3>
            <FormatCards
              formats={canExportPhotos ? FORMATS : FORMATS.filter(entry => entry.id === 'xlsx')}
              format={format}
              onChange={setFormat}
              availability={availability}
              disabled={isExporting}
              t={t}
            />
          </section>

          <section className="space-y-3 rounded-2xl bg-gray-50 p-3 dark:bg-gray-900">
            {format === 'xlsx' && (
              <ExcelOptions
                preview={preview}
                loading={previewLoading}
                columns={columns}
                onToggleColumn={toggleColumn}
                onResetColumns={() => setColumns(defaultColumnsOf(preview?.sheets || []))}
                t={t}
              />
            )}
            {format !== 'xlsx' && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('Include metadata')}</span>
                <input
                  type="checkbox"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                  disabled={isExporting}
                />
              </div>
            )}
            {format === 'pptx' && (
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">{t('PPTX layout')}</p>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 4].map(layout => (
                    <button
                      key={layout}
                      type="button"
                      onClick={() => setPptxLayout(layout)}
                      className={[
                        'rounded-xl border px-3 py-2 text-sm font-medium transition',
                        pptxLayout === layout
                          ? 'border-blue-500 bg-white text-blue-700 shadow-sm dark:bg-blue-950/70 dark:text-blue-200'
                          : 'border-gray-200 text-gray-600 hover:bg-white/70 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                      ].join(' ')}
                      disabled={isExporting}
                    >
                      {layout} {layout === 1 ? t('photo') : t('photos')}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('Export name')}</label>
              <input
                type="text"
                value={archiveName}
                onChange={(e) => setArchiveName(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
                disabled={isExporting}
              />
            </div>
          </section>
        </div>

        <div className="border-t border-gray-200 p-5 dark:border-gray-800">
          {error && (
            <div role="alert" className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={runExport}
            disabled={blocked}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isExporting && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
            {isExporting ? t('Preparing…') : actionLabel}
          </button>

          {selectionIds.length > 0 && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <button type="button" onClick={onClearSelection} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                {t('Clear selection')}
              </button>
              {canDelete && (
                <button type="button" onClick={onDeleteSelected} className="inline-flex items-center font-medium text-red-600 hover:text-red-700">
                  {getAdminBadge()}
                  {t('Delete selection')}
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

export { LIMITS as EXPORT_LIMITS };
export default ExportPanel;
